// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useMemo } from 'react';

import { type TaskTimeDataFilterToApi, today, useEvolvePaginatedGet, workOrderStateNames } from '@evolvemep/api-ts';
import { AgGridReact } from 'ag-grid-react';

import { BasePageHeader } from 'components/Mantine/BasePageHeader';
import { useGlobalFilters } from 'components/Mantine/GlobalFilters/useGlobalFilters';
import { convertTimeFrameDataToRequestParams } from 'components/Mantine/TimeFramePicker/helpers';
import { AgGridContainer } from 'helpers/ag-grid/AgGridContainer';
import { TABLE_VIEW_TYPE } from 'helpers/ag-grid/TableViewPanel/constants';
import { useServerSideGrid } from 'helpers/ag-grid/useServerSideGrid';
import { getTaskDataColumnDefs } from 'modules/Shop/Insights/TaskData/columnDefs';

const filterStates = workOrderStateNames.filter((n) => n === 'In Progress' || n === 'Completed');

export const TaskDataPage = () => {
  const { facilityIds, projectIds, workOrderStateIds, timeFrame } = useGlobalFilters({
    facilities: true,
    projects: true,
    timeFrame: {
      defaultTimeFrame: 'This Year',
    },
    workOrderStates: filterStates,
  });

  const params = useMemo<TaskTimeDataFilterToApi>(
    () => ({
      facilityIds: facilityIds.length === 0 ? [] : facilityIds,
      projectIds: projectIds.length === 0 ? [] : projectIds,
      workOrderStateIds: workOrderStateIds.length === 0 ? [] : workOrderStateIds,
      ...convertTimeFrameDataToRequestParams(timeFrame),
      currentDate: today(),
    }),
    [facilityIds, projectIds, workOrderStateIds, timeFrame],
  );

  const { fetchPage, abortController, setDefaultOpts } = useEvolvePaginatedGet('shop/task/timedata', {
    defaultConfig: { params },
  });

  useEffect(() => {
    setDefaultOpts({ defaultConfig: { params } });
  }, [setDefaultOpts, params]);

  const colDef = useMemo(() => getTaskDataColumnDefs(), []);

  const { agGridProps } = useServerSideGrid({
    colDef,
    tableName: 'task-data',
    tableViewType: TABLE_VIEW_TYPE.TASK_DATA_INSIGHTS,
    exportFileName: 'Task Data',
    fetchPage,
    abortController,
    rowId: 'taskId',
  });

  return (
    <>
      <BasePageHeader title="Task Data" noHistory />
      <AgGridContainer>
        {(applyWrap) => <AgGridReact {...agGridProps} onFirstDataRendered={applyWrap} alwaysMultiSort={true} />}
      </AgGridContainer>
    </>
  );
};
